import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

export const NAV_LINKS = [
  { t: "Platforms", to: "/platforms" },
  { t: "Executive", to: "/executive" },
  { t: "Bridge", to: "/bridge" },
  { t: "ARIA", href: "/#aria" },
  { t: "Journey", href: "/#journey" },
  { t: "Founder", href: "/#founder" },
  { t: "About", to: "/about" },
  { t: "Trust & Technology", to: "/trust" },
  { t: "Contact", to: "/contact" },
];

export const MobileNavMenu = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <button className="nav-burger" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn">
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
      {open && (
        <div className="mobile-menu" data-testid="mobile-menu">
          {NAV_LINKS.map((l) =>
            l.to ? (
              <Link key={l.t} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-link-${l.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {l.t}
              </Link>
            ) : (
              <a key={l.t} href={l.href} onClick={() => setOpen(false)} data-testid={`mobile-link-${l.t.toLowerCase()}`}>
                {l.t}
              </a>
            )
          )}
          <button
            className="nav-cta mobile-menu-cta"
            onClick={() => { setOpen(false); navigate("/contact?type=beta"); }}
            data-testid="mobile-menu-cta-btn"
          >
            Get Beta Access
          </button>
        </div>
      )}
    </>
  );
};

export const SiteNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <header className="nav-wrap" data-testid="site-navigation">
      <div className="nav-inner">
        <Link to="/" className="nav-brand" data-testid="site-nav-logo">
          <span className="brand-mark"><img src="/brand/camelion-emblem.png" alt="" /></span>
          <span className="brand-text">Camelion<span className="accent">AI</span></span>
        </Link>
        <ul className="nav-links">
          {NAV_LINKS.map((l) => {
            const testid = `site-nav-${l.t.toLowerCase().replace(/[^a-z]+/g, "-")}`;
            return (
              <li key={l.t}>
                {l.to ? (
                  <Link to={l.to} className={pathname === l.to || pathname.startsWith(l.to + "/") ? "is-active" : ""} data-testid={testid}>
                    {l.t}
                  </Link>
                ) : (
                  <a href={l.href} data-testid={testid}>{l.t}</a>
                )}
              </li>
            );
          })}
        </ul>
        <div className="nav-right">
          <button className="nav-cta" onClick={() => navigate("/contact?type=beta")} data-testid="site-nav-cta-btn">
            Get Beta Access
          </button>
          <MobileNavMenu />
        </div>
      </div>
    </header>
  );
};

const FOOT_COLS = [
  {
    h: "Products",
    links: [
      { t: "Platforms Overview", to: "/platforms" },
      { t: "ProsthoAI", to: "/platforms/prosthoai" },
      { t: "ZidaMD", to: "/platforms/zidamd" },
      { t: "ReimbursePro", to: "/platforms/reimbursepro" },
    ],
  },
  {
    h: "Company",
    links: [
      { t: "About CamelionAI", to: "/about" },
      { t: "Trust & Technology", to: "/trust" },
      { t: "The Bridge", to: "/bridge" },
      { t: "Executive Intelligence", to: "/executive" },
    ],
  },
  {
    h: "Trust & Legal",
    links: [
      { t: "Privacy Policy", to: "/privacy" },
      { t: "Terms of Use", to: "/terms" },
      { t: "Security Posture", to: "/trust" },
    ],
  },
];

export const SiteFooter = () => (
  <footer className="sfoot" data-testid="site-footer">
    <div className="sfoot-inner">
      <div className="sfoot-brand">
        <Link to="/" className="nav-brand">
          <span className="brand-mark"><img src="/brand/camelion-emblem.png" alt="" /></span>
          <span className="brand-text">Camelion<span className="accent">AI</span></span>
        </Link>
        <p className="sfoot-tagline">Built to adapt. Designed to perform.</p>
        <p className="sfoot-note">Adaptive healthcare intelligence, designed for HIPAA-aligned deployment.</p>
      </div>
      {FOOT_COLS.map((c) => (
        <div className="sfoot-col" key={c.h}>
          <div className="sfoot-h">{c.h}</div>
          {c.links.map((l) => (
            <Link key={l.t} to={l.to} className="sfoot-link" data-testid={`footer-link-${l.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {l.t}
            </Link>
          ))}
        </div>
      ))}
      <div className="sfoot-col">
        <div className="sfoot-h">Contact</div>
        <Link to="/contact" className="sfoot-link" data-testid="footer-link-contact">Contact Us</Link>
        <a href="mailto:ronik@camelionai.com" className="sfoot-link">ronik@camelionai.com</a>
        <span className="sfoot-link sfoot-muted">Boca Raton, FL</span>
      </div>
    </div>
    <div className="sfoot-bottom">
      <span>© 2026 CamelionAI LLC</span>
      <span className="dot">·</span>
      <span>Patent Pending USPTO</span>
      <span className="dot">·</span>
      <span>Hiscox Insured</span>
      <span className="dot">·</span>
      <span>Designed for HIPAA-aligned deployment</span>
    </div>
  </footer>
);

export const SitePage = ({ children, testId }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("emergent-badge")?.remove();
  }, []);
  return (
    <div className="sp-root" data-testid={testId}>
      <div className="grid-bg" />
      <SiteNav />
      <main className="sp-main">{children}</main>
      <SiteFooter />
    </div>
  );
};

export const PageHead = ({ label, title, lede }) => (
  <div className="sp-head">
    <div className="section-label"><span className="label-line" /> {label}</div>
    <h1 className="section-title sp-title">{title}</h1>
    {lede && <p className="section-lede">{lede}</p>}
  </div>
);
